// RigSentinel Context Engine — Context Health (RESERVED)
//
// ARCHITECTURAL PLACEHOLDER · Phase RCE-1
// ---------------------------------------
// Future home of the Context Health Dashboard. The dashboard reports how
// well the engine is feeding the rest of the app, surfacing things like:
//
//   * Context Resolution Coverage   — % of expected fields resolved
//   * Inherited Fields              — count of fields flowing in from parents
//   * Derived Fields                — count of computed fields
//   * Unknown Fields                — count of fields nobody publishes yet
//   * Conflicts                     — fields with disagreeing sources
//   * Duplicate Entries Prevented   — fields the user no longer had to retype
//   * AI Context Completeness       — % of fields an AI consumer would have
//                                     access to for a given report type
//
// Phase RCE-1 ships only a thin `summariseHealth(ctx)` function that returns
// the bare four counters used by the Context Inspector today. Everything
// above (duplicate-prevented counter, AI completeness, time-series, etc.)
// is deliberately deferred — the surface area is reserved here so future
// phases plug in without an architectural refactor.

import { FIELD_REGISTRY, classifyField } from "../classification";
import { resolve } from "../resolver";

/**
 * Snapshot the live health of the Context Engine against a context. Pure
 * read-only function. Walks every registered field and tallies how many
 * resolved exact / inherited / derived / override / conflict / unknown.
 *
 * Intentionally minimal in RCE-1 — extended dashboards layer on top.
 */
export function summariseHealth(ctx = {}) {
  const total = Object.keys(FIELD_REGISTRY).length;
  const buckets = {
    exact: 0, inherited: 0, derived: 0,
    override: 0, conflict: 0, unknown: 0,
    documentOnly: 0,
  };
  for (const field of Object.keys(FIELD_REGISTRY)) {
    if (classifyField(field) === "document-only") {
      buckets.documentOnly += 1;
      continue;
    }
    const r = resolve(field, ctx);
    const k = buckets[r.confidence] !== undefined ? r.confidence : "unknown";
    buckets[k] += 1;
  }
  const resolved = buckets.exact + buckets.inherited + buckets.derived + buckets.override;
  const eligible = total - buckets.documentOnly;
  return {
    total,
    eligible,
    resolvedCount: resolved,
    coveragePct: eligible > 0 ? Math.round((resolved / eligible) * 100) : 0,
    byConfidence: buckets,
    // Reserved tiles — values intentionally null until the dashboard ships.
    duplicateEntriesPrevented: null,
    aiContextCompletenessPct: null,
  };
}
